import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldPlus, PaperPlaneRight, SignOut, IdentificationCard,
  FileText, ClipboardText, Buildings, Question, Check, Files, Users, Phone, MagnifyingGlass, Umbrella,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { EHealthCard, PolicyCard, ClaimsCard, HospitalsCard, FaqsCard, DocumentsCard, DependentsCard, TicketsCard, BookingsCard, OPDPlansCard, CheckupPackagesCard, MyOPDCard, FamilyECardsCard, MemberLookupCard, PolicyTermsCard } from "@/components/insurehub/ChatCards";
import { ClaimForm } from "@/components/insurehub/ClaimForm";
import { TicketForm } from "@/components/insurehub/TicketForm";
import { CallbackForm } from "@/components/insurehub/CallbackForm";
import { BookingForm } from "@/components/insurehub/BookingForm";
import { InsuranceEnquiryForm } from "@/components/insurehub/InsuranceEnquiryForm";
import { CashlessCheckForm } from "@/components/insurehub/CashlessCheckForm";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const QUICK_ACTIONS = [
  { label: "OPD plans", icon: ShieldPlus, text: "Show OPD plans" },
  { label: "Health check-up plans", icon: ShieldPlus, text: "Show health check-up packages" },
  { label: "Show my e-card", icon: IdentificationCard, text: "Show my e-health card" },
  { label: "My coverage", icon: FileText, text: "What does my policy cover?" },
  { label: "Track my claims", icon: ClipboardText, text: "Show my claim status" },
  { label: "Submit a claim", icon: FileText, text: "I want to submit a claim" },
  { label: "Find hospitals", icon: Buildings, text: "Find network hospitals near me" },
  { label: "Cashless check", icon: Buildings, text: "Check if a hospital is cashless covered" },
  { label: "Raise a ticket", icon: Question, text: "I want to raise a service ticket" },
  { label: "Request a callback", icon: Phone, text: "I want to request a callback" },
  { label: "Insurance enquiry", icon: Umbrella, text: "I want an insurance enquiry" },
  { label: "My tickets", icon: ClipboardText, text: "Show my service tickets" },
  { label: "Book check-up", icon: FileText, text: "I want to book a home health check-up" },
  { label: "Look up member", icon: MagnifyingGlass, text: "Look up a member by employee code" },
  { label: "Terms & conditions", icon: FileText, text: "Show my policy terms and conditions" },
  { label: "My OPD", icon: ShieldPlus, text: "Show my OPD plan" },
  { label: "Family e-cards", icon: IdentificationCard, text: "Show family e-cards" },
  { label: "My bookings", icon: FileText, text: "Show my health check-up bookings" },
  { label: "My documents", icon: Files, text: "Show my policy documents" },
  { label: "My family", icon: Users, text: "Who are my dependents?" },
  { label: "FAQs", icon: Question, text: "Show me frequently asked questions" },
];

function nowTime() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3 bg-white border border-zinc-200 rounded-2xl rounded-bl-sm w-fit shadow-sm">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-2 h-2 rounded-full bg-emerald-600"
          animate={{ opacity: [0.3, 1, 0.3], y: [0, -3, 0] }}
          transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

function BookingLinkCard({ data }) {
  const url = data?.url || "https://forms.gle/o8HgNo6oBK5MJzWUA";
  return (
    <div data-testid="booking-link-card" className="bg-white border border-emerald-200 rounded-2xl rounded-bl-sm shadow-sm p-4 max-w-sm">
      <div className="flex items-center gap-2 mb-1">
        <ShieldPlus size={18} weight="fill" className="text-emerald-600" />
        <p className="font-heading font-semibold text-emerald-950">Home Health Check-up</p>
      </div>
      <p className="text-sm text-zinc-600 mb-3">
        Book a home sample collection by <b>Lupin Diagnostics</b>. Fill this quick form and we'll collect your sample at home & share the report.
      </p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="booking-form-link-btn"
        className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2.5 transition-colors"
      >
        <FileText size={16} weight="bold" /> Book Health Check-up
      </a>
    </div>
  );
}

function CardRenderer({ card, onClaimSubmitted, onTicketSubmitted, onBookingSubmitted, user }) {
  if (!card) return null;
  switch (card.type) {
    case "ecard": return <EHealthCard data={card.data} />;
    case "policy": return <PolicyCard data={card.data} />;
    case "claims": return <ClaimsCard data={card.data} />;
    case "hospitals": return <HospitalsCard data={card.data} />;
    case "faqs": return <FaqsCard data={card.data} />;
    case "documents": return <DocumentsCard data={card.data} />;
    case "dependents": return <DependentsCard data={card.data} />;
    case "claim_form": return <ClaimForm onSubmitted={onClaimSubmitted} />;
    case "ticket_form": return <TicketForm onSubmitted={onTicketSubmitted} />;
    case "booking_form": return <BookingForm user={user} onSubmitted={onBookingSubmitted} />;
    case "tickets": return <TicketsCard data={card.data} />;
    case "bookings": return <BookingsCard data={card.data} />;
    case "opd_plans": return <OPDPlansCard data={card.data} />;
    case "my_opd": return <MyOPDCard data={card.data} />;
    case "family_ecards": return <FamilyECardsCard data={card.data} />;
    case "callback_form": return <CallbackForm />;
    case "insurance_enquiry": return <InsuranceEnquiryForm />;
    case "cashless_check": return <CashlessCheckForm />;
    case "checkup_packages": return <CheckupPackagesCard data={card.data} />;
    case "member_lookup": return <MemberLookupCard />;
    case "policy_terms": return <PolicyTermsCard data={card.data} />;
    default: return null;
  }
}

export default function Chat() {
  const { user, logout } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setMessages([
      {
        id: "welcome",
        role: "assistant",
        text: `Hi ${user?.name?.split(" ")[0] || "there"}! 👋 I'm the Aarogya Assist Chatbot. I can help you check coverage, view your e-card, track or file claims and find network hospitals. What would you like to do?`,
        time: nowTime(),
      },
    ]);
  }, [user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const send = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || typing) return;
    setInput("");
    const userMsg = { id: `u-${Date.now()}`, role: "user", text: trimmed, time: nowTime() };
    setMessages((m) => [...m, userMsg]);
    setTyping(true);
    try {
      const { data } = await api.post("/chat/message", { session_id: sessionId, text: trimmed });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [
        ...m,
        { id: `a-${Date.now()}`, role: "assistant", text: data.reply, card: data.card, time: nowTime() },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { id: `e-${Date.now()}`, role: "assistant", text: "Something went wrong. Please try again.", time: nowTime() },
      ]);
    } finally {
      setTyping(false);
      inputRef.current?.focus();
    }
  };

  const onClaimSubmitted = (claim) => {
    setMessages((m) => [
      ...m,
      {
        id: `a-${Date.now()}`,
        role: "assistant",
        text: `Great! Your claim ${claim.claim_number} has been submitted and is now marked "${claim.status}". You can track it anytime by asking "claim status".`,
        card: { type: "claims", data: [claim] },
        time: nowTime(),
      },
    ]);
  };

  const onTicketSubmitted = (ticket) => {
    setMessages((m) => [
      ...m,
      {
        id: `a-${Date.now()}`,
        role: "assistant",
        text: `🎫 Ticket ${ticket.ticket_number} raised (${ticket.category}). Our support team will follow up soon. Ask "my tickets" anytime to check its status.`,
        time: nowTime(),
      },
    ]);
  };

  const onBookingSubmitted = (booking) => {
    setMessages((m) => [
      ...m,
      {
        id: `a-${Date.now()}`,
        role: "assistant",
        text: `🩺 Booking ${booking.booking_number} confirmed! Lupin Diagnostics will collect your sample at home on ${booking.preferred_date} (${booking.preferred_time}) and share the report. Ask "my bookings" to track it.`,
        time: nowTime(),
      },
    ]);
  };

  return (
    <div className="h-screen flex flex-col bg-[#EFECE4]">
      {/* Header */}
      <header className="bg-emerald-950 text-white px-4 py-3 flex items-center justify-between shadow-md z-20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white overflow-hidden relative ring-1 ring-white/20">
            <img src="/logo.png" alt="Aarogya Assist" className="w-full h-full object-cover scale-110" data-testid="brand-logo" />
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-emerald-950" />
          </div>
          <div>
            <p className="font-heading font-semibold leading-tight" data-testid="bot-name">Aarogya Assist Chatbot</p>
            <p className="text-[11px] text-emerald-300">online · replies instantly</p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="chat-menu" className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 px-3 py-1.5 transition-colors">
              <span className="text-sm font-medium hidden sm:inline">{user?.name?.split(" ")[0]}</span>
              <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-bold">
                {user?.name?.[0]}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-600 cursor-pointer">
              <SignOut size={16} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto chat-bg px-3 sm:px-6 py-6" data-testid="messages-area">
        <div className="max-w-2xl mx-auto flex flex-col gap-3">
          {/* Health benefits promo — always visible entry point */}
          <div data-testid="health-plans-promo"
            className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
                <ShieldPlus size={22} weight="fill" className="text-white" />
              </div>
              <div className="flex-1">
                <p className="font-heading font-semibold text-emerald-950 text-sm">Explore health benefits</p>
                <p className="text-xs text-zinc-600 mt-0.5">Add an OPD plan for everyday care, or book a discounted home health check-up.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid="promo-opd-plans" onClick={() => send("Show OPD plans")} disabled={typing}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3.5 py-2 transition-colors disabled:opacity-50">
                    <ShieldPlus size={14} weight="fill" /> Explore OPD plans
                  </button>
                  <button data-testid="promo-checkup-packages" onClick={() => send("Show health check-up packages")} disabled={typing}
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50 text-xs font-medium px-3.5 py-2 transition-colors disabled:opacity-50">
                    <FileText size={14} /> Health check-up packages
                  </button>
                </div>
              </div>
            </div>
          </div>
          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[85%] flex flex-col gap-2 ${m.role === "user" ? "items-end" : "items-start"}`}>
                  {m.text && (
                    <div
                      data-testid={`msg-${m.role}`}
                      className={`px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${
                        m.role === "user"
                          ? "bg-emerald-900 text-white rounded-2xl rounded-br-sm"
                          : "bg-white text-zinc-800 border border-zinc-200 rounded-2xl rounded-bl-sm"
                      }`}
                    >
                      {m.text}
                      <span className={`ml-2 text-[10px] align-bottom inline-flex items-center gap-0.5 ${m.role === "user" ? "text-emerald-200/70" : "text-zinc-400"}`}>
                        {m.time}
                        {m.role === "user" && <Check size={12} weight="bold" />}
                      </span>
                    </div>
                  )}
                  {m.card && <CardRenderer card={m.card} onClaimSubmitted={onClaimSubmitted} onTicketSubmitted={onTicketSubmitted} onBookingSubmitted={onBookingSubmitted} user={user} />}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {typing && (
            <div className="flex justify-start" aria-live="polite">
              <TypingIndicator />
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-[#EFECE4] px-3 sm:px-6 pt-2 border-t border-black/5">
        <div className="max-w-2xl mx-auto flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {QUICK_ACTIONS.map((qa) => (
            <button
              key={qa.label}
              onClick={() => send(qa.text)}
              disabled={typing}
              data-testid={`quick-${qa.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="shrink-0 flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white text-emerald-800 text-xs font-medium px-3 py-1.5 hover:bg-emerald-50 hover:-translate-y-0.5 transition-[transform,background-color] disabled:opacity-50"
            >
              <qa.icon size={15} />
              {qa.label}
            </button>
          ))}
        </div>
      </div>

      {/* Input */}
      <div className="bg-[#EFECE4] px-3 sm:px-6 py-3">
        <form
          onSubmit={(e) => { e.preventDefault(); send(input); }}
          className="max-w-2xl mx-auto flex items-center gap-2"
        >
          <input
            ref={inputRef}
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message…"
            className="flex-1 rounded-full bg-white border border-zinc-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-shadow"
          />
          <button
            type="submit"
            disabled={typing || !input.trim()}
            data-testid="chat-send"
            className="w-12 h-12 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white flex items-center justify-center shrink-0 disabled:opacity-40 transition-colors"
          >
            <PaperPlaneRight size={20} weight="fill" />
          </button>
        </form>
        <p className="max-w-2xl mx-auto text-center text-[10px] text-zinc-500/70 mt-2" data-testid="copyright">
          © {new Date().getFullYear()} Aarogya Innovate Pvt Ltd
        </p>
      </div>
    </div>
  );
}
